import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { sanitizeHtml } from 'utils';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { KeyboardDatePicker } from '@material-ui/pickers';
import Typography from '@material-ui/core/Typography';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { CAN_CHECK_UNCHECK_HOUSE } from 'providers/permissions';
import { PhaseStructureTypeEnum } from '@vatos-pas/common';
import { makeStyles } from '@material-ui/core/styles';
import Chip from '@material-ui/core/Chip/Chip';
import { useDataProvider } from 'react-admin';
import OptionsHistoryModal, {
  formatPaintPayoutAmount,
  historyItemToRow,
} from './OptionsHistoryModal';
import InfoIcon from '@material-ui/icons/Info';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import IconButton from '@material-ui/core/IconButton';
import { TrimOptionStatus } from './TrimOptionStatus';

const styles = makeStyles({
  input: {
    margin: '8px 0px',
  },
  inputNoMarginRight: {
    margin: '8px 0px 8px 15px',
  },
  black: {
    color: 'black !important',
  },
  whiteBoxMobile: {
    backgroundColor: 'white',
    borderRadius: '10px',
    border: '1px solid lightgray',
    padding: '10px',
  },
  titleTab: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px',
    borderBottom: '1px solid black',
  },
  titleFont: {
    fontWeight: 'bold',
    fontSize: '16px',
  },
});

interface MobileJobInfoProps {
  theJob: any;
  checkHouse: any;
}

const getJobAddressInfo = (theJob: any) => {
  if (!theJob?.job) return '';
  return `${theJob.job.city || ''} - ${theJob.job.state || ''} - ${
    theJob.job.zip || ''
  }`;
};

export const MobileJobInfo: React.FC<MobileJobInfoProps> = ({
  theJob,
  checkHouse,
}) => {
  const classes = styles();
  const { hasPermission } = PermissionsProvider.useContainer();
  const dataProvider = useDataProvider();
  const [actualPanel, setActualPanel] = useState<number>(1);

  // Modal states for options history
  const [openInteriorHistory, setOpenInteriorHistory] = useState(false);
  const [openExteriorHistory, setOpenExteriorHistory] = useState(false);
  const [interiorHistoryData, setInteriorHistoryData] = useState<any[]>([]);
  const [exteriorHistoryData, setExteriorHistoryData] = useState<any[]>([]);
  const [loadingInterior, setLoadingInterior] = useState(false);
  const [loadingExterior, setLoadingExterior] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && actualPanel === 1) {
      toggleActualPanel(2);
    }
    if (isRightSwipe && actualPanel === 2) {
      toggleActualPanel(1);
    }
  };

  const inputProps = {
    InputLabelProps: {
      shrink: true,
      className: classes.black,
    },
    InputProps: {
      className: classes.black,
    },
  };

  const getPanelVisibility = (panelNumber: number) => {
    return panelNumber === actualPanel ? 'block' : 'none';
  };

  const toggleActualPanel = (panelNumber: number) => {
    if (panelNumber !== actualPanel) {
      setActualPanel(panelNumber);
    }
  };

  // Load history data functions (shared logic from desktop)
  const loadInteriorHistory = async () => {
    if (!theJob?.id) return;
    setLoadingInterior(true);
    try {
      const result = await dataProvider.getList(
        'job-interior-exterior-options-history-report',
        {
          filter: { jobId: theJob.id },
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'id', order: 'DESC' },
        },
      );
      const interiorData = (result.data || []).filter(
        (item: any) => item.typeOption === 'Interior',
      );
      setInteriorHistoryData(interiorData);
    } catch (error) {
      console.error('Error loading interior history:', error);
      setInteriorHistoryData([]);
    } finally {
      setLoadingInterior(false);
    }
  };

  const loadExteriorHistory = async () => {
    if (!theJob?.id) return;
    setLoadingExterior(true);
    try {
      const result = await dataProvider.getList(
        'job-interior-exterior-options-history-report',
        {
          filter: { jobId: theJob.id },
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'id', order: 'DESC' },
        },
      );
      const exteriorData = (result.data || []).filter(
        (item: any) => item.typeOption === 'Exterior',
      );
      setExteriorHistoryData(exteriorData);
    } catch (error) {
      console.error('Error loading exterior history:', error);
      setExteriorHistoryData([]);
    } finally {
      setLoadingExterior(false);
    }
  };

  const handleInteriorIconClick = () => {
    loadInteriorHistory();
    setOpenInteriorHistory(true);
  };

  const handleExteriorIconClick = () => {
    loadExteriorHistory();
    setOpenExteriorHistory(true);
  };

  return (
    <>
      <Box pl={1}>
        <Box display="flex" width="100%" flexDirection="row">
          <Box width="50%" display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={theJob.job?.houseMarked || false}
                  onChange={(event: any) => checkHouse(event.target.checked)}
                  name="isMonday"
                  disabled={
                    !hasPermission(CAN_CHECK_UNCHECK_HOUSE) ||
                    theJob.job?.houseMarked
                  }
                  color="primary"
                />
              }
              label="Mark House"
            />
          </Box>
          <Box width="50%">
            {theJob.job?.dateHouseMarked && (
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="MM/dd/yyyy"
                margin="normal"
                id="date-picker-inline"
                label="House marked on"
                fullWidth
                onChange={() => {
                  // Date house marked is read-only, managed by checkHouse function
                }}
                value={
                  theJob.job?.dateHouseMarked
                    ? new Date(theJob.job.dateHouseMarked)
                    : null
                }
                disabled
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
            )}
          </Box>
        </Box>
        <Box width="100%" display="flex" alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                checked={theJob.job?.garageFirst || false}
                name="garageFirst"
                disabled
                color="primary"
              />
            }
            label="Garage"
          />
          {theJob?.job?.phaseStructureType ===
            PhaseStructureTypeEnum.DrywallAndPaint && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={theJob.job?.hasTrim || false}
                  name="hasTrim"
                  disabled
                  color="primary"
                />
              }
              label="Trim"
            />
          )}
          {theJob?.job?.phaseStructureType ===
            PhaseStructureTypeEnum.DrywallAndPaint && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={theJob.job?.hasExteriorPaint || false}
                  name="hasExteriorPaint"
                  disabled
                  color="primary"
                />
              }
              label="Exterior Paint"
            />
          )}
        </Box>
        <Box width="100%" display="flex" alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                checked={theJob.job?.highCeiling || false}
                name="highCeiling"
                disabled
                color="primary"
              />
            }
            label="High Ceiling"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={theJob.job?.screwInspection || false}
                name="screwInspection"
                disabled
                color="primary"
              />
            }
            label="Screw Inspection"
          />
        </Box>
        <>
          {theJob.job?.hasInteriorOptions && (
            <Box width="100%" display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={theJob.job?.interiorOptionsFinished || false}
                    name="interiorOptionsFinished"
                    disabled
                    color="primary"
                  />
                }
                label="Paint Interior Options"
              />
              <InfoIcon
                style={{
                  marginLeft: '2px',
                  cursor: 'pointer',
                  color: '#1976d2',
                  fontSize: '20px',
                }}
                onClick={handleInteriorIconClick}
                titleAccess="Paint Interior Options History"
              />
            </Box>
          )}
          {theJob.job?.hasExteriorOptions && (
            <Box width="100%" display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={theJob.job?.exteriorOptionsFinished || false}
                    name="exteriorOptionsFinished"
                    disabled
                    color="primary"
                  />
                }
                label="Paint Exterior Options"
              />
              <InfoIcon
                style={{
                  marginLeft: '2px',
                  cursor: 'pointer',
                  color: '#1976d2',
                  fontSize: '20px',
                }}
                onClick={handleExteriorIconClick}
                titleAccess="Paint Exterior Options History"
              />
            </Box>
          )}
          <TrimOptionStatus
            fullWidth
            jobId={theJob.id}
            hasTrimOptions={theJob.job?.hasTrimOptions}
            trimOptionsFinished={theJob.job?.trimOptionsFinished}
            trimOptionsFinishedJobPhaseId={
              theJob.job?.trimOptionsFinishedJobPhaseId
            }
            jobPhases={theJob.jobPhases}
          />
        </>
      </Box>
      <Box
        className={classes.whiteBoxMobile}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <Box m={2}>
          <Box display={getPanelVisibility(1)} width="100%">
            <Box>
              <Box
                width="100%"
                display="flex"
                alignItems="flex-end"
                gridGap={8}
              >
                <Box flex={1}>
                  <TextField
                    label="Subdivision"
                    fullWidth
                    {...inputProps}
                    disabled
                    className={classes.input}
                    value={theJob.job?.subdivisionName || ''}
                  />
                </Box>
                <Box flex={1}>
                  <TextField
                    label="Builder"
                    fullWidth
                    {...inputProps}
                    disabled
                    className={classes.input}
                    value={theJob.job?.builderName || ''}
                  />
                </Box>
              </Box>

              <Box width="100%">
                <TextField
                  label="Current Supplier"
                  fullWidth
                  {...inputProps}
                  disabled
                  className={classes.input}
                  value={theJob.job?.currentSupplierName || ''}
                />
              </Box>

              <Box
                width="100%"
                display="flex"
                alignItems="flex-end"
                gridGap={8}
              >
                <Box flex={1}>
                  <TextField
                    label="Building #"
                    fullWidth
                    {...inputProps}
                    disabled
                    className={classes.input}
                    value={theJob.job?.buildingNumber || ''}
                  />
                </Box>
                <Box flex={1}>
                  <TextField
                    label="Lot"
                    fullWidth
                    {...inputProps}
                    disabled
                    className={classes.input}
                    value={theJob.job?.lot || ''}
                  />
                </Box>
                <Box flex={1}>
                  <TextField
                    label="Block"
                    fullWidth
                    {...inputProps}
                    disabled
                    className={classes.input}
                    value={theJob.job?.block || ''}
                  />
                </Box>
              </Box>

              <TextField
                label="Address"
                fullWidth
                {...inputProps}
                disabled
                value={theJob.job?.address1 || ''}
                className={classes.input}
              />
              <TextField
                label="City, State, Zip"
                fullWidth
                {...inputProps}
                disabled
                className={classes.input}
                value={getJobAddressInfo(theJob)}
              />
            </Box>
          </Box>

          <Box display={getPanelVisibility(2)} width="100%">
            <Box>
              <Box width="100%" style={{ borderBottom: '1px dotted gray' }}>
                <Box>
                  <Typography style={{ fontSize: 12 }}>Supervisors</Typography>
                </Box>
                <Box display="flex" flexWrap="wrap">
                  {theJob.job?.supervisorFullNames &&
                    theJob.job.supervisorFullNames
                      .split(',')
                      .map((item: string) => (
                        <Chip
                          style={{ margin: '5px 5px 5px 0px' }}
                          key={item}
                          label={item}
                        />
                      ))}
                </Box>
              </Box>
              <Box
                width="100%"
                display="flex"
                alignItems="flex-end"
                gridGap={8}
              >
                <Box flex={1}>
                  <TextField
                    label="Job ID #"
                    fullWidth
                    {...inputProps}
                    disabled
                    value={theJob.job?.idNumber || ''}
                    className={classes.input}
                  />
                </Box>
                <Box flex={1}>
                  <TextField
                    label="Model"
                    fullWidth
                    {...inputProps}
                    disabled
                    className={classes.input}
                    value={theJob.job?.modelName || ''}
                  />
                </Box>
              </Box>
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="MM/dd/yyyy"
                margin="normal"
                id="date-picker-inline"
                label="Project Approval - Due Date"
                fullWidth
                disabled
                className={classes.input}
                value={
                  theJob.job?.dateDue ? new Date(theJob.job.dateDue) : null
                }
                onChange={(date: MaterialUiPickersDate) => date}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
                {...inputProps}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        display="flex"
        width="100%"
        justifyContent="space-between"
        alignItems="center"
      >
        {actualPanel === 2 && (
          <IconButton onClick={() => toggleActualPanel(1)}>
            <ChevronLeftIcon />
          </IconButton>
        )}
        {actualPanel === 1 && <Box />}
        {actualPanel === 1 && (
          <IconButton onClick={() => toggleActualPanel(2)}>
            <ChevronRightIcon />
          </IconButton>
        )}
        {actualPanel === 2 && <Box />}
      </Box>

      <Box mb={2}>
        <Box className={classes.titleTab}>
          <Typography className={classes.titleFont}>Options</Typography>
        </Box>
        {theJob.job?.options && (
          <div
            className="preview"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(theJob.job.options),
            }}
          ></div>
        )}
      </Box>

      {/* Options History Modals */}
      <OptionsHistoryModal
        open={openInteriorHistory}
        onClose={() => setOpenInteriorHistory(false)}
        title="Paint Interior Options"
        rows={interiorHistoryData.map(historyItemToRow)}
        loading={loadingInterior}
        payoutAmount={formatPaintPayoutAmount(
          theJob.job?.interiorCalculatedCost,
        )}
      />

      <OptionsHistoryModal
        open={openExteriorHistory}
        onClose={() => setOpenExteriorHistory(false)}
        title="Paint Exterior Options"
        rows={exteriorHistoryData.map(historyItemToRow)}
        loading={loadingExterior}
        payoutAmount={formatPaintPayoutAmount(
          theJob.job?.exteriorCalculatedCost,
        )}
      />
    </>
  );
};

export default MobileJobInfo;
